import type { QueryFunction, QueryKey } from '@tanstack/react-query'

import type { DtActionsAtom } from '@/components/pages/backoffice/_klb-penyuluh/_kecamatan/state/atoms'
import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type { PenyuluhBPPDataType } from './types'
import type { MutationPenyuluhBppValues } from './validations'

import { anggaran } from './anggaran'
import { fotoBangunan } from './foto-bangunan'
import { kegiatan } from './kegiatan'
import { klasifikasi } from './klasifikasi'
import { lahanPercontohan } from './lahan-percontohan'
import { penghargaan } from './penghargaan'
import { penyuluh } from './penyuluh'
import { potensiEkonomi } from './potensi-ekonomi'
import { potensiWilayah } from './potensi-wilayah'
import { sarpras } from './sarpas'
import { wilayahKecamatan } from './wilayah-kecamatan'

interface InfiniteQueryOptions<T> {
  queryKey: QueryKey
  initialPageParam: number
  getNextPageParam: (lastPage: ServiceGetAllResponse<T>) => number | undefined
  queryFn: QueryFunction<ServiceGetAllResponse<T>, QueryKey, number>
}

const BPP_QUERY_KEY = (key?: string | Record<string, any>) =>
  ['bpp', key] as const

const getAll = (
  params?: DtActionsAtom
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<PenyuluhBPPDataType>>
> => ({
  queryKey: BPP_QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<PenyuluhBPPDataType>>
      >('/kelembagaan/bpp', {
        params: {
          page: params?.page,
          s: params?.q ? `${params.q}` : undefined,
          mode: 'and',
          per_page: params?.perPage || undefined,
          id_provinsi: params?.province?.value || undefined,
          id_kota: params?.city?.value || undefined,
          id_kecamatan: params?.district?.value || undefined
        }
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getAllInfinite = (
  params?: Record<string, any>
): InfiniteQueryOptions<PenyuluhBPPDataType[]> => ({
  queryKey: BPP_QUERY_KEY({ ...params, type: 'bpp' }),
  initialPageParam: 1,
  getNextPageParam: (
    lastPage: ServiceGetAllResponse<PenyuluhBPPDataType[]>
  ) => {
    const meta = lastPage.meta

    if (!meta) return undefined
    return (meta?.current_page ?? 0) < (meta?.last_page ?? 0)
      ? (meta?.current_page ?? 0) + 1
      : undefined
  },
  queryFn: async ({ pageParam }: { pageParam: number }) => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<PenyuluhBPPDataType[]>
      >(`/kelembagaan/bpp`, {
        params: {
          ...params,
          identifier: undefined,
          page: pageParam
        }
      })

      return {
        message: response.data.message,
        meta: response.data.meta,
        data: response.data.data
      }
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<PenyuluhBPPDataType>> => ({
  queryKey: BPP_QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<PenyuluhBPPDataType>
      >(`/kelembagaan/bpp/${id}`)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const create = (): ServiceMutationOptionType<
  void,
  MutationPenyuluhBppValues
> => ({
  mutationFn: async (data: MutationPenyuluhBppValues) => {
    try {
      const {
        id_desa,
        id_jenis_bpp,
        city_id,
        district_id,
        province_id,
        longitude,
        latitude,
        is_non_administratif,
        ...body
      } = data

      const response = await api.post('/kelembagaan/bpp', {
        ...body,
        koordinator_penyuluh: {
          ...data.koordinator_penyuluh,
          id_penyuluh:
            data.koordinator_penyuluh?.id_penyuluh?.value || undefined
        },
        id_jenis_bpp: id_jenis_bpp.value || undefined,
        id_desa: id_desa.value || undefined,
        city_id: city_id.value || undefined,
        district_id: district_id.value || undefined,
        province_id: province_id.value || undefined,
        longitude: longitude?.toString() || undefined,
        latitude: latitude?.toString() || undefined,
        is_administrative: is_non_administratif ? false : true
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const update = ({
  bppId
}: {
  bppId: string
}): ServiceMutationOptionType<void, MutationPenyuluhBppValues> => ({
  mutationFn: async (data: MutationPenyuluhBppValues) => {
    try {
      const {
        id_desa,
        id_jenis_bpp,
        city_id,
        district_id,
        province_id,
        longitude,
        latitude,
        is_non_administratif,
        ...body
      } = data

      const response = await api.put(`/kelembagaan/bpp/${bppId}`, {
        ...body,
        koordinator_penyuluh: {
          ...data.koordinator_penyuluh,
          id_penyuluh:
            data.koordinator_penyuluh?.id_penyuluh?.value || undefined
        },
        id_jenis_bpp: id_jenis_bpp.value || undefined,
        id_desa: id_desa.value || undefined,
        city_id: city_id.value || undefined,
        district_id: district_id.value || undefined,
        province_id: province_id.value || undefined,
        longitude: longitude?.toString() || undefined,
        latitude: latitude?.toString() || undefined,
        is_administrative: is_non_administratif ? false : true
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const deleteData = (): ServiceMutationOptionType<void, { bppId: string }> => ({
  mutationFn: async (data: { bppId: string }) => {
    try {
      const response = await api.delete(`/kelembagaan/bpp/${data.bppId}`)

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const exportData = (filterParams?: DtActionsAtom) => ({
  mutationFn: async () => {
    try {
      const response = await api.get<{
        status: string
        url?: string | null
        job_id?: string
        message?: string
        progress?: number
      }>(`/kelembagaan/bpp/export`, {
        params: {
          page: filterParams?.page,
          s: filterParams?.q ? `${filterParams.q}` : undefined,
          mode: 'and',
          per_page: filterParams?.perPage || undefined,
          id_provinsi: filterParams?.province?.value || undefined,
          id_kota: filterParams?.city?.value || undefined,
          id_kecamatan: filterParams?.district?.value || undefined
        }
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const penyuluhBpp = {
  queryKey: [BPP_QUERY_KEY(undefined)[0]],
  getAll,
  getById,
  create,
  update,
  deleteData,
  getAllInfinite,
  exportData,
  detail: {
    sarpras,
    potensiEkonomi,
    lahanPercontohan,
    penghargaan,
    anggaran,
    kegiatan,
    potensiWilayah,
    klasifikasi,
    wilayahKecamatan,
    penyuluh,
    fotoBangunan
  }
}
